import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Clock, Instagram, Send } from "lucide-react";
import api from "@/lib/api";

const STUDIO = {
  address: "Via Giuseppe Saragat, 12a",
  city: "06049 Spoleto PG",
  phone: "0743 050842",
  hours: [
    { d: "Lunedì – Venerdì", h: "09:30 – 13:00 · 15:00 – 18:00" },
    { d: "Sabato – Domenica", h: "Chiuso" },
  ],
  instagramLabel: "@planit_studio",
  instagramUrl: "https://www.instagram.com/planit_studio/",
  mapsEmbed:
    "https://www.google.com/maps?q=Via+Giuseppe+Saragat+12a,+06049+Spoleto+PG&output=embed",
  mapsLink:
    "https://www.google.com/maps/search/?api=1&query=Via+Giuseppe+Saragat+12a,+06049+Spoleto+PG",
};

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", _hp: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Messaggio inviato. Ci sentiamo presto!");
      setForm({ name: "", email: "", phone: "", message: "", _hp: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore, riprova.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
        {/* Title */}
        <div className="text-center">
          <h1 className="font-serif text-5xl lg:text-6xl font-bold text-stone-900" data-testid="contact-title">
            Contattaci
          </h1>
          <div className="mt-4 w-16 h-[2px] bg-[#D06D4D] mx-auto" aria-hidden="true" />
        </div>

        {/* Two columns */}
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          {/* Left: info */}
          <div className="bg-white border border-stone-200 rounded-2xl p-8 lg:p-10 shadow-[8px_8px_24px_rgba(28,25,23,0.08)]" data-testid="contact-info">
            <h2 className="font-serif text-2xl lg:text-3xl text-stone-900">Informazioni di Contatto</h2>
            <div className="mt-8 space-y-7">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg bg-[#D06D4D]/10 border border-[#D06D4D]/20 flex items-center justify-center flex-shrink-0">
                  <MapPin size={20} className="text-[#D06D4D]" strokeWidth={1.7} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-stone-500">Indirizzo</div>
                  <a
                    href={STUDIO.mapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 block text-stone-900 hover:text-[#D06D4D] transition-colors"
                    data-testid="contact-address"
                  >
                    {STUDIO.address}<br />
                    {STUDIO.city}<br />
                    <span className="text-stone-600 text-sm">Provincia di Perugia</span>
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg bg-[#D06D4D]/10 border border-[#D06D4D]/20 flex items-center justify-center flex-shrink-0">
                  <Phone size={20} className="text-[#D06D4D]" strokeWidth={1.7} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-stone-500">Telefono</div>
                  <a
                    href={`tel:+39${STUDIO.phone.replace(/\s/g, "")}`}
                    className="mt-1 block text-stone-900 hover:text-[#D06D4D] transition-colors"
                    data-testid="contact-phone"
                  >
                    {STUDIO.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg bg-[#D06D4D]/10 border border-[#D06D4D]/20 flex items-center justify-center flex-shrink-0">
                  <Clock size={20} className="text-[#D06D4D]" strokeWidth={1.7} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-stone-500">Orari di apertura</div>
                  <ul className="mt-1 text-stone-900" data-testid="contact-hours">
                    {STUDIO.hours.map((h, i) => (
                      <li key={h} className="text-sm">
                        <span className="font-medium">{h.d}:</span>{" "}
                        <span className="text-stone-700">{h.h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg bg-[#D06D4D]/10 border border-[#D06D4D]/20 flex items-center justify-center flex-shrink-0">
                  <Instagram size={20} className="text-[#D06D4D]" strokeWidth={1.7} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-stone-500">Social Media</div>
                  <a
                    href={STUDIO.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 block text-stone-900 hover:text-[#D06D4D] transition-colors"
                    data-testid="contact-instagram"
                  >
                    {STUDIO.instagramLabel}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="bg-white border border-stone-200 rounded-2xl p-8 lg:p-10 shadow-[8px_8px_24px_rgba(28,25,23,0.08)]" data-testid="contact-form-wrapper">
            <h2 className="font-serif text-2xl lg:text-3xl text-stone-900">Inviaci un Messaggio</h2>
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <label className="block text-xs uppercase tracking-widest text-stone-600 mb-2">Nome e Cognome</label>
                <input
                  data-testid="contact-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-transparent border-b border-stone-300 focus:border-[#D06D4D] outline-none py-2.5 text-stone-900"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-stone-600 mb-2">Email</label>
                <input
                  data-testid="contact-email"
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-transparent border-b border-stone-300 focus:border-[#D06D4D] outline-none py-2.5 text-stone-900"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-stone-600 mb-2">Telefono</label>
                <input
                  data-testid="contact-phone-input"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-transparent border-b border-stone-300 focus:border-[#D06D4D] outline-none py-2.5 text-stone-900"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-stone-600 mb-2">Messaggio</label>
                <textarea
                  data-testid="contact-message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-transparent border-b border-stone-300 focus:border-[#D06D4D] outline-none py-2.5 text-stone-900 resize-none"
                />
              </div>
              {/* Honeypot: hidden from real users, filled only by bots */}
              <input
                type="text"
                name="_hp"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={form._hp}
                onChange={(e) => setForm({ ...form, _hp: e.target.value })}
                style={{ position: "absolute", left: "-9999px", top: "-9999px", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
              />
              <button
                data-testid="contact-submit"
                type="submit"
                disabled={sending}
                className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#D06D4D] hover:bg-[#b95a3d] text-white px-8 py-3 rounded-full text-sm font-semibold disabled:opacity-60 transition-colors"
              >
                <Send size={14} /> {sending ? "Invio…" : "Invia Messaggio"}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Full-width Google Maps */}
      <div className="w-full" data-testid="contact-map">
        <iframe
          title="Plan It Studio — Sede"
          src={STUDIO.mapsEmbed}
          width="100%"
          height="450"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="block border-0"
          allowFullScreen
        />
      </div>
    </div>
  );
}
